
import { Component, Prop, Watch, Mixins, Inject } from 'vue-property-decorator'
import { PluralResponse } from 'coloquent/dist/response/PluralResponse'
import { Vessel } from '@/models/Vessel'
import { Company } from '@/models/Company'
import Icon from '@/components/Icon.vue'
import debounce from 'lodash.debounce'
import ListItem from '@/interfaces/ListItem'
import Testable from '@/mixins/Testable'
import { namespace } from 'vuex-class'
import i18n from '@/i18n'

const Vessels = namespace('vessels')
const Companies = namespace('companies')
const Brokers = namespace('brokers')
const Auth = namespace('auth')

@Component({
	components: {
		Icon
	}
})
export default class SpecificVesselPicker extends Mixins(Testable) {
	@Inject('$validator') $validator

	@Prop({ default: false })
	returnModel!: boolean

	@Prop({ default: false })
	isUsedForQuickGroupCreation!: boolean

	@Prop({ default: false })
	includeNoDataTile!: boolean

	@Prop({ default: false })
	disablePicker!: boolean

	@Prop({ default: true })
	addVessels!: boolean

	@Prop({ default: false })
	addLogisticServiceProviders!: boolean

	@Prop({ default: false })
	addUnregisteredBrokers!: boolean

	@Prop({ default: true })
	addUnregisteredVessels!: boolean

	@Prop({ default: '' })
	vesselName!: string

	@Prop({ default: false })
	showHint!: boolean

	@Prop({ default: false })
	isRequired!: boolean

	@Prop({ default: true })
	allowNew!: boolean

	@Vessels.Mutation('cache')
	cacheVessels

	@Vessels.Getter('peekAll')
	peekVessels

	@Companies.Mutation('cache')
	cacheCompanies

	@Companies.Getter('peekAll')
	peekLogisticServiceProviders

	@Brokers.Mutation('cache')
	cacheBrokers

	@Brokers.Getter('peekAll')
	peekBrokers

	@Auth.Getter('currentCompany')
	currentCompany!: Company

	@Auth.Getter('hasUpToDateMembership')
	hasUpToDateMembership!: boolean

	dataLoading: boolean = false

	currentName: string = ''
	debouncedVesselsQuery = () => {}

	specificVessels: Array<Vessel> = []
	logisticServiceProviders: Array<Company> = []
	unregisteredBrokers: Array<Company> = []
	items: Array<Vessel | Company> = []

	query: string = ''

	created() {
		this.debouncedVesselsQuery = debounce(this.searchItems, 300)
		this.currentName = this.vesselName
	}

	@Watch('vesselName')
	setCurrentName() {
		if (this.currentName !== this.vesselName) {
			this.currentName = this.vesselName
		}
	}

	@Watch('query')
	triggerQuery() {
		this.debouncedVesselsQuery()
	}

	async searchItems() {
		this.dataLoading = true
		try {
			if (this.query && this.query.trim().length >= 3) {
				let vesselsQuery: Promise<Array<Vessel>> = Promise.resolve([])
				let specificLogisticsServiceProvidersQuery: Promise<Array<Company>> = Promise.resolve([])
				let unregisteredBrokersQuery: Promise<Array<Company>> = Promise.resolve([])
				let peekedVessels = []
				let peekedLogisticServiceProviders = []
				let peekedBrokers = []

				if (this.addVessels || this.addUnregisteredVessels) {
					vesselsQuery = this.getVessels(this.addVessels, this.addUnregisteredVessels)
					peekedVessels = this.peekVessels().filter((vessel) => {
						if (vessel.isRegistered && !this.addVessels) return false
						if (!vessel.isRegistered && !this.addUnregisteredVessels) return false
						return (vessel.name || '').toLowerCase().includes(this.query.toLowerCase())
					})
				}

				if (this.addLogisticServiceProviders) {
					specificLogisticsServiceProvidersQuery = this.getSpecificLogisticServiceProviders()
					peekedLogisticServiceProviders = this.peekLogisticServiceProviders().filter((broker) =>
						(broker.name || '').toLowerCase().includes(this.query.toLowerCase())
					)
				}

				if (this.addUnregisteredBrokers) {
					unregisteredBrokersQuery = this.getUnregisteredBrokers()
					peekedBrokers = this.peekBrokers().filter((broker) =>
						(broker.name || '').toLowerCase().includes(this.query.toLowerCase())
					)
				}

				this.items = [...peekedVessels, ...peekedLogisticServiceProviders, ...peekedBrokers]

				this.specificVessels = await vesselsQuery
				this.logisticServiceProviders = await specificLogisticsServiceProvidersQuery
				this.unregisteredBrokers = await unregisteredBrokersQuery

				this.items = [...this.specificVessels, ...this.logisticServiceProviders, ...this.unregisteredBrokers]
			} else if (!this.query || this.query.trim().length === 0) {
				this.clearSearchResults()
				this.$emit('input', null)
			}
		} finally {
			this.dataLoading = false
		}
	}

	async emitOpenCreator() {
		this.query = ''
		this.$emit('openCreator')
	}

	async update(e) {
		if (e?.type) {
			if (this.returnModel) {
				let item: Vessel | Company
				if (e.type === new Vessel().getJsonApiType()) {
					item = await Vessel.fetchLazy(e.value)
					this.cacheVessels([item])
				} else {
					item = (await Company.find(e.value)).getData() as Company
				}
				if (this.isUsedForQuickGroupCreation) {
					this.specificVessels = []
					this.query = ''
				}
				this.$emit('input', item)
			} else {
				this.$emit('input', e)
			}
		}
	}

	private mapItemsForCombobox(items: Array<ListItem>) {
		return items.map((item) => {
			return {
				value: item.getApiId(),
				text: item.title,
				type: item.getJsonApiType(),
				icon: this.getIcon(item),
				sub: item.sub,
				unregistered: item instanceof Vessel && !item.isRegistered,
				profileCompleteness: item instanceof Vessel ? item.profilePercentage : null
			}
		})
	}

	public getIcon(item) {
		return item.getJsonApiType() === 'companies' ? 'logistic-service-provider' : 'ship'
	}

	private async getVessels(addRegistered: boolean, addUnregistered: boolean): Promise<Array<Vessel>> {
		let include = 'none';
		if (this.hasUpToDateMembership) {
			include = 'vesselAttributes';
		}

		let query = await Vessel.option('include', include).where('search', this.query)
		if (addRegistered && addUnregistered) {
			// don't pass parameter as default with load both
		} else if (addRegistered) {
			query = query.where('isRegistered', 'true')
		} else if (addUnregistered) {
			query = query.where('isRegistered', 'false')
		}
		const vessels = (await query.get()).getData() as Array<Vessel>
		this.cacheVessels(vessels)
		return vessels
	}

	private async getSpecificLogisticServiceProviders(): Promise<Array<Company>> {
		const query = (await Company.option('include', 'none')
			.where('search', this.query)
			.where('companyType', 'logisticServiceProvider')
			.get()) as PluralResponse
		const companies = query.getData() as Array<Company>
		this.cacheCompanies(companies)
		return companies
	}

	private async getUnregisteredBrokers(): Promise<Array<Company>> {
		const query = (await this.currentCompany
			.brokers()
			.option('include', 'none')
			.where('search', this.query)
			.get()) as PluralResponse
		const companies = query.getData() as Array<Company>
		this.cacheBrokers(companies)
		return companies
	}

	clearSearchResults() {
		this.specificVessels = []
		this.logisticServiceProviders = []
		this.items = []
	}

	clearSelection() {
		this.currentName = ''
	}

	get queryLongEnough() {
		return this.query && this.query.length >= 3
	}

	get hint(): string {
		if (
			this.addVessels &&
			!this.addUnregisteredVessels &&
			!this.addLogisticServiceProviders &&
			!this.addUnregisteredBrokers
		) {
			return i18n.t('cargo.publication.vessels_hint') as string
		}
		return ''
	}

	get searchVessels(): boolean {
		return this.addVessels || this.addUnregisteredVessels
	}
}
